import { get } from 'lodash-es';
import aromatherapyIncludedIcon from '../images/addons/aromatherapy.svg';
import heatTherapyIncludedIcon from '../images/addons/heat-therapy.svg';
import percussionTherapyIncludedIcon from '../images/addons/percussion-therapy.svg';
import { BOOKING_ADDONS_PAGE_PREFERENCE_API_KEYS } from './app-contants';
import {
  getDefaultComplimentaryBookingPreferenceParams,
  isAromatherapyScentValue,
  isNewBookingGuest,
} from './booking-addon-preferences';

const HARD_CODED_ADDON_ICON_BY_RULE_KEY = {
  cbd_oil: '/images/addons/cbd-oil.png',
  concentrated_cbd: '/images/addons/cbd-lotion.png',
  athlete_relief: '/images/addons/muscle-cream.png',
  hand_treatment: '/images/addons/hand-treatment.png',
  foot_treatment: '/images/addons/foot-treatment.png',
  eye_treatment: '/images/addons/under-eye-patches.png',
  scalp_ritual: '/images/addons/scalp-treatment.png',
  red_light_therapy: '/images/addons/red-light-therapy.png',
  pemf_therapy: '/images/addons/pemf-therapy.png',
  magnesium: '/images/addons/magnesium.png',
};

const HARD_CODED_ADDON_DISPLAY_LABEL_BY_RULE_KEY = {
  athlete_relief: 'Athlete Relief CBD',
};

/** Shown above modal CTA (Got It / Add) for CBD add-ons. */
const HARD_CODED_ADDON_MODAL_CBD_NOTE_BY_RULE_KEY = {
  concentrated_cbd: '*Approximately 150 mg of CBD*',
  athlete_relief: '*Approximately 133 mg of CBD',
};

const HARD_CODED_ADDON_EXCLUSION_RULES = {
  cbd_oil: {
    minorRestricted: true,
    prenatalRestricted: true,
    incompatibleWith: ['magnesium'],
  },
  athlete_relief: {
    minorRestricted: true,
    prenatalRestricted: true,
    incompatibleWith: ['hand_treatment', 'foot_treatment', 'scalp_ritual'],
  },
  concentrated_cbd: {
    minorRestricted: true,
    prenatalRestricted: true,
    incompatibleWith: [
      'hand_treatment',
      'foot_treatment',
      'scalp_ritual',
      'magnesium',
    ],
  },
  eye_treatment: {
    incompatibleWith: ['red_light_therapy'],
  },
  hand_treatment: {
    onlyDurationMinutes: [80],
    incompatibleWith: ['scalp_ritual', 'foot_treatment'],
  },
  foot_treatment: {
    onlyDurationMinutes: [80],
    incompatibleWith: ['hand_treatment', 'scalp_ritual'],
  },
  scalp_ritual: {
    prenatalRestricted: true,
    incompatibleWith: ['hand_treatment', 'foot_treatment', 'red_light_therapy'],
  },
  red_light_therapy: {
    incompatibleWith: ['eye_treatment', 'scalp_ritual'],
  },
  pemf_therapy: {},
  magnesium: {
    prenatalRestricted: true,
    incompatibleWith: ['cbd_oil', 'concentrated_cbd'],
  },
};

function normalizeAddonRuleKeyFromText(textRaw) {
  const text = String(textRaw || '').toLowerCase();
  if (!text) return null;
  if (text.includes('magnesium')) return 'magnesium';
  if (text.includes('concentrated cbd') || text.includes('cbd lotion')) {
    return 'concentrated_cbd';
  }
  if (text.includes('cbd oil')) return 'cbd_oil';
  if (
    text.includes('athlete relief') ||
    text.includes('athlet relief') ||
    (text.includes('athlet') && text.includes('relief')) ||
    text.includes('muscle cream')
  ) {
    return 'athlete_relief';
  }
  if (
    text.includes('shine bright') ||
    text.includes('bright eyes') ||
    (text.includes('eye') && text.includes('treatment'))
  ) {
    return 'eye_treatment';
  }
  if (
    text.includes('hand treatment') ||
    text.includes('farm house fresh hand')
  ) {
    return 'hand_treatment';
  }
  if (
    text.includes('foot treatment') ||
    text.includes('farm house fresh foot')
  ) {
    return 'foot_treatment';
  }
  if (text.includes('serenity scalp') || text.includes('scalp ritual')) {
    return 'scalp_ritual';
  }
  if (text.includes('red light')) {
    return 'red_light_therapy';
  }
  if (text.includes('pemf')) {
    return 'pemf_therapy';
  }
  return null;
}

/** Member and non-member price are both $0 (complimentary add-on). */
export function isZeroPriceAddon(addon) {
  if (!addon || typeof addon !== 'object') return false;
  const m = Number(get(addon, 'member_price'));
  const n = Number(get(addon, 'non_member_price'));
  return Number.isFinite(m) && m === 0 && Number.isFinite(n) && n === 0;
}

/**
 * Keep only add-on rows that are active for the given location (API may return extras).
 */
export function filterAddonServiceItemsForLocation(items, locationId) {
  if (!Array.isArray(items)) return [];
  return items.filter(item => {
    if (!item || typeof item !== 'object') return false;
    if (item.is_active === 0 || item.is_active === false) return false;
    if (item.active === false) return false;
    if (item.enabled === false) return false;
    if (
      item.location_id != null &&
      String(item.location_id) !== String(locationId)
    ) {
      return false;
    }
    return true;
  });
}

/**
 * Redux may still hold add-ons from another location; only use the list when it
 * matches the currently selected location.
 */
export function getServiceAddonsForSelectedLocation(appointmentData) {
  const { serviceAddons, serviceAddonsLocationId, selectedLocation } =
    appointmentData || {};
  const selectedId = get(selectedLocation, 'id');
  const list = Array.isArray(serviceAddons) ? serviceAddons : [];
  if (selectedId == null || serviceAddonsLocationId == null) {
    return [];
  }
  if (String(selectedId) !== String(serviceAddonsLocationId)) {
    return [];
  }
  return list;
}

/**
 * Comma-separated addon_type ids for therapist list / availability (includes
 * paid selection in selectedServiceAddon plus complimentary picks).
 */
/** True when add-on is CBD oil (label/heading from Zenoti / API). */
export function isCbdAddon(addon) {
  if (!addon || typeof addon !== 'object') return false;
  const text = [
    get(addon, 'addon_type.label'),
    get(addon, 'addon_type.heading'),
    get(addon, 'addon_type.name'),
    get(addon, 'name'),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return text.includes('cbd');
}

/** Paid add-ons that minors cannot select (see HARD_CODED_ADDON_EXCLUSION_RULES). */
export function isAddonRestrictedForMinor(addon) {
  if (!addon || typeof addon !== 'object') return false;
  const ruleKey = getAddonRuleKey(addon);
  if (ruleKey) {
    return Boolean(
      (HARD_CODED_ADDON_EXCLUSION_RULES[ruleKey] || {}).minorRestricted,
    );
  }
  return isCbdAddon(addon);
}

export function shouldRestrictAddonsForBooking({
  pregnancy,
  guestUnder18,
} = {}) {
  return Boolean(pregnancy || guestUnder18);
}

export function filterAddonsForBookingRestrictions(
  addons,
  { pregnancy, guestUnder18 } = {},
) {
  const list = Array.isArray(addons) ? addons : [];
  if (!shouldRestrictAddonsForBooking({ pregnancy, guestUnder18 })) {
    return list;
  }
  // Show the full catalog; pregnancy/minor rules disable tiles via getAddonDisabledReason.
  return list;
}

function addonLabelText(addon) {
  return [
    get(addon, 'addon_type.label'),
    get(addon, 'addon_type.heading'),
    get(addon, 'addon_type.name'),
    get(addon, 'name'),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function getAddonRuleKey(addon) {
  if (!addon || typeof addon !== 'object') return null;
  return normalizeAddonRuleKeyFromText(addonLabelText(addon));
}

export function getAddonCircleIconUrl(addon) {
  const key = getAddonRuleKey(addon);
  return key ? HARD_CODED_ADDON_ICON_BY_RULE_KEY[key] || '' : '';
}

/** Bundled design SVGs for the included row (do not use API URLs — they often 404). */
const BOOKING_INCLUDED_ICON_BY_API_KEY = {
  aromatherapy: aromatherapyIncludedIcon,
  heat_therapy: heatTherapyIncludedIcon,
  percussion_therapy: percussionTherapyIncludedIcon,
};

/** Included massage features: always use bundled SVG artwork from design files. */
export function getBookingIncludedFeatureIconUrl(apiKey) {
  if (!apiKey) return '';
  const asset = BOOKING_INCLUDED_ICON_BY_API_KEY[apiKey];
  if (!asset) return '';
  if (typeof asset === 'string') return asset;
  if (typeof asset === 'object' && asset.default) {
    return typeof asset.default === 'string' ? asset.default : '';
  }
  return '';
}

export function getBookingDurationMinutes(appointmentData) {
  const service =
    get(appointmentData, 'selectedService') ||
    get(appointmentData, 'crossSelectedService') ||
    get(appointmentData, 'selectedServiceDetails') ||
    {};
  const duration = Number(get(service, 'duration'));
  return Number.isFinite(duration) ? duration : null;
}

/** True when add-on requires a session length the guest has not selected. */
export function isAddonRestrictedForDuration(addon, durationMinutes) {
  const ruleKey = getAddonRuleKey(addon);
  if (!ruleKey) return false;
  const rules = HARD_CODED_ADDON_EXCLUSION_RULES[ruleKey];
  if (
    !rules ||
    !Array.isArray(rules.onlyDurationMinutes) ||
    !rules.onlyDurationMinutes.length
  ) {
    return false;
  }
  const duration = Number(durationMinutes);
  if (!Number.isFinite(duration)) return false;
  return !rules.onlyDurationMinutes.includes(duration);
}

export const ADDON_MODAL_EIGHTY_MIN_MAIN_SQUEEZE_NOTE =
  'Available with an 80-min Main Squeeze only.';

/** Info (?) modal note for premium enhancements that require an 80-min Main Squeeze. */
export function getAddonModalDurationAvailabilityNote(addon) {
  if (!addon) return '';
  const ruleKey = getAddonRuleKey(addon);
  if (!ruleKey) return '';
  const allowed = (HARD_CODED_ADDON_EXCLUSION_RULES[ruleKey] || {})
    .onlyDurationMinutes;
  if (!Array.isArray(allowed) || allowed.length !== 1 || allowed[0] !== 80) {
    return '';
  }
  return ADDON_MODAL_EIGHTY_MIN_MAIN_SQUEEZE_NOTE;
}

export function getAddonDisabledReason(
  addon,
  {
    durationMinutes = null,
    pregnancy = false,
    guestUnder18 = false,
    selectedPaidAddons = [],
  } = {},
) {
  const ruleKey = getAddonRuleKey(addon);
  if (!ruleKey) return null;
  const rules = HARD_CODED_ADDON_EXCLUSION_RULES[ruleKey];
  if (!rules) return null;

  if (isAddonRestrictedForDuration(addon, durationMinutes)) {
    const allowed = rules.onlyDurationMinutes || [];
    if (allowed.length === 1) {
      return `This enhancement is only available with ${
        allowed[0]
      }-minute massages.`;
    }
    return 'This enhancement is not available for your current session length.';
  }
  if (rules.minorRestricted && guestUnder18) {
    return 'This enhancement is not available for guests under 18.';
  }
  if (rules.prenatalRestricted && pregnancy) {
    return 'This enhancement is not available for prenatal sessions.';
  }

  const selectedRuleKeys = (Array.isArray(selectedPaidAddons)
    ? selectedPaidAddons
    : []
  )
    .map(getAddonRuleKey)
    .filter(Boolean);
  if (!selectedRuleKeys.length) return null;

  const selectedIncompatible = selectedRuleKeys.some(k =>
    (rules.incompatibleWith || []).includes(k),
  );
  if (selectedIncompatible) {
    return 'This enhancement cannot be combined with your current selections.';
  }
  const thisAddonIncompatibleWithSelected = selectedRuleKeys.some(k =>
    (
      (HARD_CODED_ADDON_EXCLUSION_RULES[k] || {}).incompatibleWith || []
    ).includes(ruleKey),
  );
  if (thisAddonIncompatibleWithSelected) {
    return 'This enhancement cannot be combined with your current selections.';
  }

  return null;
}

/**
 * Guest-facing copy when confirm strips add-ons that are invalid for the
 * current session (e.g. Hand Treatment on a 50-min Mid Squeeze).
 */
export function buildRemovedRestrictedAddonNotice(removedEntries) {
  const list = Array.isArray(removedEntries) ? removedEntries : [];
  if (!list.length) return null;
  if (list.length === 1) {
    const { addon, reason } = list[0];
    const label = getAddonCircleLabel(addon);
    return `${label} was removed from this booking. ${reason}`;
  }
  const parts = list.map(({ addon, reason }) => {
    const label = getAddonCircleLabel(addon);
    return `${label} (${reason})`;
  });
  return `These enhancements aren't available for this session and were removed: ${parts.join(
    '; ',
  )}.`;
}

/**
 * Split paid booking add-ons into those allowed for the current session vs
 * those blocked by duration / prenatal / under-18 rules (not combo conflicts).
 */
export function stripRestrictedBookingPaidAddons(
  paidAddons,
  { durationMinutes = null, pregnancy = false, guestUnder18 = false } = {},
) {
  const list = Array.isArray(paidAddons) ? paidAddons : [];
  const restrictionCtx = {
    durationMinutes,
    pregnancy: Boolean(pregnancy),
    guestUnder18: Boolean(guestUnder18),
    selectedPaidAddons: [],
  };
  const allowed = [];
  const removed = [];
  list.forEach(addon => {
    const reason = getAddonDisabledReason(addon, restrictionCtx);
    if (reason) {
      removed.push({ addon, reason });
    } else {
      allowed.push(addon);
    }
  });
  return {
    allowed,
    removed,
    notice: buildRemovedRestrictedAddonNotice(removed),
  };
}

/** Complimentary scent choice rows (DO/HEAL/LIVE/SLEEP), not the main aromatherapy add-on. */
export function isAromatherapyScentAddon(addon) {
  if (!addon || typeof addon !== 'object') return false;
  if (!isZeroPriceAddon(addon)) return false;
  const type = get(addon, 'addon_type') || {};
  const shortLabel = [
    get(type, 'label'),
    get(type, 'name'),
    get(type, 'title'),
    get(type, 'heading'),
  ]
    .filter(Boolean)
    .map(s => String(s).trim())
    .find(Boolean);
  if (shortLabel && isAromatherapyScentValue(shortLabel)) return true;
  const text = addonLabelText(addon);
  if (
    /preference|desired scent|scent choice/.test(text) &&
    /aromatherapy|aroma therapy/.test(text)
  ) {
    return true;
  }
  return false;
}

/** Complimentary catalog rows that map to profile prefs, not paid add-on tiles. */
export function isComplimentaryPreferenceAddon(addon) {
  if (!addon || typeof addon !== 'object') return false;
  // Priced enhancements stay on the paid add-ons row even when labels overlap prefs.
  if (!isZeroPriceAddon(addon)) {
    return false;
  }
  if (isAromatherapyScentAddon(addon)) return false;
  const text = addonLabelText(addon);
  if (isDeepTissueAddon(addon)) return true;
  if (text.includes('oil') && text.includes('lotion')) return true;
  if (text.includes('heated') && text.includes('table')) return true;
  if (text.includes('heat therapy') || text.includes('heat pad')) return true;
  if (text.includes('percussion')) return true;
  if (text.includes('aromatherapy') || text.includes('aroma therapy')) {
    return true;
  }
  return false;
}

export function isDeepTissueAddon(addon) {
  if (!addon || typeof addon !== 'object') return false;
  const text = addonLabelText(addon);
  return text.includes('deep tissue');
}

export function isDeepTissuePreferenceEnabled(bookingMassagePreferences) {
  if (
    !bookingMassagePreferences ||
    typeof bookingMassagePreferences !== 'object'
  ) {
    return false;
  }
  const v = bookingMassagePreferences.deep_tissue;
  return v === 1 || v === '1' || String(v).toLowerCase() === 'yes';
}

export function resolveDeepTissueAddonForBooking(
  serviceAddons,
  bookingMassagePreferences,
  selectedMassageType = null,
) {
  if (
    !isDeepTissuePreferenceEnabled(bookingMassagePreferences) &&
    !isDeepTissuePressureSelected(selectedMassageType)
  ) {
    return null;
  }
  const list = Array.isArray(serviceAddons) ? serviceAddons : [];
  return list.find(isDeepTissueAddon) || null;
}

/** Pressure step "Deep" maps to complimentary deep-tissue add-on when catalog has one. */
export function isDeepTissuePressureSelected(selectedMassageType) {
  const name = String(get(selectedMassageType, 'name') || '').toLowerCase();
  return name.includes('deep');
}

/** True when stored pref value means yes (receipt / complimentary add-on). */
export function isBookingPreferenceValueAffirmative(apiKey, storedValue) {
  if (storedValue === null || storedValue === undefined) return false;
  if (apiKey === 'percussion_therapy') {
    return String(storedValue).toLowerCase() === 'yes';
  }
  const n = Number(storedValue);
  if (Number.isFinite(n)) return n === 1;
  const s = String(storedValue).toLowerCase();
  return s === 'yes' || s === 'true' || s === '1';
}

export function isBookingPreferenceEnabled(apiKey, bookingMassagePreferences) {
  if (
    !bookingMassagePreferences ||
    typeof bookingMassagePreferences !== 'object'
  ) {
    return false;
  }
  if (apiKey === 'deep_tissue') {
    return isDeepTissuePreferenceEnabled(bookingMassagePreferences);
  }
  const v = bookingMassagePreferences[apiKey];
  if (v === null || v === undefined) return false;
  if (apiKey === 'percussion_therapy') {
    return String(v).toLowerCase() === 'yes';
  }
  if (apiKey === 'aromatherapy') {
    const upper = String(v).toUpperCase();
    if (['DO', 'HEAL', 'LIVE', 'SLEEP'].includes(upper)) return true;
  }
  return (
    v === 1 ||
    v === '1' ||
    String(v).toLowerCase() === 'yes' ||
    String(v).toLowerCase() === 'true'
  );
}

/** Map complimentary catalog row to add-ons-page preference apiKey. */
export function findPreferenceApiKeyForFreeAddon(addon) {
  if (!addon || typeof addon !== 'object') return null;
  const text = addonLabelText(addon);
  if (/heat therapy|heat pad/.test(text)) return 'heat_therapy';
  if (/percussion/.test(text)) return 'percussion_therapy';
  if (/aromatherapy|aroma therapy/.test(text)) return 'aromatherapy';
  if (isDeepTissueAddon(addon)) return 'deep_tissue';
  return null;
}

function findCatalogAddonForPreference(apiKey, serviceAddons) {
  const list = Array.isArray(serviceAddons) ? serviceAddons : [];
  const text = addonLabelText;

  const predicates = {
    heat_therapy: a =>
      /heat therapy|heat pad/.test(text(a)) &&
      isComplimentaryPreferenceAddon(a),
    percussion_therapy: a =>
      /percussion/.test(text(a)) && isComplimentaryPreferenceAddon(a),
    aromatherapy: a =>
      /aromatherapy|aroma therapy/.test(text(a)) &&
      isComplimentaryPreferenceAddon(a) &&
      !isAromatherapyScentAddon(a),
    deep_tissue: a => isDeepTissueAddon(a),
  };

  const pred = predicates[apiKey];
  if (!pred) return null;
  return list.find(pred) || null;
}

/**
 * Booking prefs used to resolve complimentary add-ons, with defaults for new
 * signups and guests who have not set heat / percussion / aromatherapy yet.
 */
export function getEffectiveBookingMassagePreferences(
  bookingMassagePreferences,
) {
  let prefs = bookingMassagePreferences;
  if (prefs && typeof prefs.toJS === 'function') {
    prefs = prefs.toJS();
  }
  const base = prefs && typeof prefs === 'object' ? { ...prefs } : {};
  const complimentaryKeys = [
    'heat_therapy',
    'percussion_therapy',
    'aromatherapy',
  ];
  const hasComplimentarySelection = complimentaryKeys.some(
    key => base[key] !== null && base[key] !== undefined,
  );

  if (!isNewBookingGuest() && hasComplimentarySelection) {
    return base;
  }

  const defaults = getDefaultComplimentaryBookingPreferenceParams();
  return { ...defaults, ...base };
}

/**
 * Complimentary add-ons implied by booking preferences (add-ons page + deep pressure).
 */
export function collectFreeAddonsFromBookingPreferences(
  serviceAddons,
  bookingMassagePreferences,
  selectedMassageType = null,
) {
  const effectivePrefs = getEffectiveBookingMassagePreferences(
    bookingMassagePreferences,
  );
  const byTypeId = new Map();
  const add = addon => {
    if (!addon) return;
    const tid = get(addon, 'addon_type.id');
    if (tid == null || tid === '') return;
    byTypeId.set(String(tid), addon);
  };

  ['heat_therapy', 'percussion_therapy', 'aromatherapy'].forEach(apiKey => {
    if (!isBookingPreferenceEnabled(apiKey, effectivePrefs)) return;
    add(findCatalogAddonForPreference(apiKey, serviceAddons));
  });

  if (
    isDeepTissuePreferenceEnabled(effectivePrefs) ||
    isDeepTissuePressureSelected(selectedMassageType)
  ) {
    add(findCatalogAddonForPreference('deep_tissue', serviceAddons));
  }

  return [...byTypeId.values()];
}

/**
 * Preference receipt rows (Heat/Percussion/Aromatherapy) duplicate free add-on
 * lines when the same catalog add-on is already in the booking free list.
 */
const PREFERENCE_FREE_LABEL_MATCHERS = {
  heat_therapy: a => /heat therapy|heat pad/.test(addonLabelText(a)),
  percussion_therapy: a => /percussion/.test(addonLabelText(a)),
  aromatherapy: a => /aromatherapy|aroma therapy/.test(addonLabelText(a)),
};

function freeListAlreadyShowsPreference(apiKey, freeList, serviceAddons) {
  const catalogAddon = findCatalogAddonForPreference(apiKey, serviceAddons);
  const catalogTypeId = catalogAddon && get(catalogAddon, 'addon_type.id');
  if (catalogTypeId != null && catalogTypeId !== '') {
    const byTypeId = freeList.some(
      a =>
        get(a, 'addon_type.id') != null &&
        String(get(a, 'addon_type.id')) === String(catalogTypeId),
    );
    if (byTypeId) return true;
  }
  const labelMatch = PREFERENCE_FREE_LABEL_MATCHERS[apiKey];
  return Boolean(labelMatch && freeList.some(labelMatch));
}

export function filterPreferenceReceiptLinesCoveredByFreeAddons(
  receiptLines,
  freeAddons,
  serviceAddons,
) {
  if (!Array.isArray(receiptLines) || !receiptLines.length) {
    return [];
  }
  const freeList = Array.isArray(freeAddons) ? freeAddons : [];
  return receiptLines.filter(row => {
    const apiKey = row && row.key;
    if (!apiKey || !BOOKING_ADDONS_PAGE_PREFERENCE_API_KEYS.includes(apiKey)) {
      return true;
    }
    return !freeListAlreadyShowsPreference(apiKey, freeList, serviceAddons);
  });
}

export function mergeBookingFreeAddonLists(...lists) {
  const byTypeId = new Map();
  lists.forEach(list => {
    (Array.isArray(list) ? list : []).forEach(addon => {
      const tid = get(addon, 'addon_type.id');
      if (tid != null && tid !== '') {
        byTypeId.set(String(tid), addon);
      }
    });
  });
  return [...byTypeId.values()];
}

export function addonsMatchByIdentity(a, b) {
  if (!a || !b) return false;
  const aId = get(a, 'id');
  const bId = get(b, 'id');
  if (aId != null && bId != null && String(aId) === String(bId)) {
    return true;
  }
  const aTypeId = get(a, 'addon_type.id');
  const bTypeId = get(b, 'addon_type.id');
  return (
    aTypeId != null && bTypeId != null && String(aTypeId) === String(bTypeId)
  );
}

export function toPlainAddonList(maybeList) {
  if (!maybeList) return [];
  if (Array.isArray(maybeList)) return maybeList;
  if (typeof maybeList.toJS === 'function') return maybeList.toJS();
  return [];
}

/** Paid add-ons chosen on the booking flow (array + legacy single selection). */
export function mergeBookingPaidAddonLists(...lists) {
  const byKey = new Map();
  lists.forEach(list => {
    (Array.isArray(list) ? list : []).forEach(addon => {
      if (!addon || isZeroPriceAddon(addon)) return;
      const id = get(addon, 'id');
      const tid = get(addon, 'addon_type.id');
      let key = null;
      if (id != null && id !== '') {
        key = `id:${id}`;
      } else if (tid != null && tid !== '') {
        key = `type:${tid}`;
      }
      if (key) byKey.set(key, addon);
    });
  });
  return [...byKey.values()];
}

export function getBookingSelectedPaidAddons(appointmentData) {
  const { bookingSelectedPaidAddons, selectedServiceAddon } =
    appointmentData || {};
  const paidList = toPlainAddonList(bookingSelectedPaidAddons);
  let legacy = selectedServiceAddon;
  if (legacy && typeof legacy.toJS === 'function') {
    legacy = legacy.toJS();
  }
  const legacyPaid =
    legacy &&
    typeof legacy === 'object' &&
    Object.keys(legacy).length > 0 &&
    get(legacy, 'addon_type.id') &&
    !isZeroPriceAddon(legacy)
      ? [legacy]
      : [];
  return mergeBookingPaidAddonLists(paidList, legacyPaid);
}

export function isAddonInBookingPaidList(addon, paidList) {
  return (Array.isArray(paidList) ? paidList : []).some(a =>
    addonsMatchByIdentity(a, addon),
  );
}

/** Toggle paid add-on; same exclusive_group allows only one at a time. */
export function toggleBookingPaidAddonSelection(paidList, addon) {
  const list = Array.isArray(paidList) ? [...paidList] : [];
  const idx = list.findIndex(a => addonsMatchByIdentity(a, addon));
  if (idx >= 0) {
    list.splice(idx, 1);
    return list;
  }
  const group = getAddonExclusiveGroup(addon);
  const withoutGroup = group
    ? list.filter(a => getAddonExclusiveGroup(a) !== group)
    : list;
  withoutGroup.push(addon);
  return withoutGroup;
}

export function rematchAddonsFromCatalog(selectedAddons, catalogList) {
  if (!Array.isArray(selectedAddons) || !selectedAddons.length) {
    return [];
  }
  if (!Array.isArray(catalogList) || !catalogList.length) {
    return selectedAddons;
  }
  return selectedAddons
    .map(f => {
      if (!f) return null;
      const match = catalogList.find(a => addonsMatchByIdentity(f, a));
      return match || null;
    })
    .filter(Boolean);
}

export function getPaidAddonListUnitPrice(addon) {
  if (!addon) return null;
  const nonMember = Number(get(addon, 'non_member_price'));
  if (Number.isFinite(nonMember)) return nonMember;
  const member = Number(get(addon, 'member_price'));
  return Number.isFinite(member) ? member : null;
}

export function sumPaidAddonsListPrice(addons) {
  return (Array.isArray(addons) ? addons : []).reduce((sum, addon) => {
    const unit = getPaidAddonListUnitPrice(addon);
    return sum + (unit != null ? unit : 0);
  }, 0);
}

/**
 * Member Premium Enhancement discount: 10% of cart list total, rounded UP
 * to the nearest whole dollar so display / CC / Squeeze Bucks stay whole.
 */
export function getMemberPremiumEnhancementDiscount(
  addons,
  isMembership,
  isAlreadyMember,
) {
  if (!(isMembership || isAlreadyMember)) return 0;
  const listSum = sumPaidAddonsListPrice(addons);
  if (listSum <= 0) return 0;
  return Math.ceil(listSum * 0.1);
}

export function getPaidAddonUnitPriceForContext(
  addon,
  isMembership,
  isAlreadyMember,
) {
  const list = getPaidAddonListUnitPrice(addon);
  if (list == null) return null;
  if (!(isMembership || isAlreadyMember)) return list;
  // Single-addon estimate (booking circles / modals). Cart totals use
  // getMemberPremiumEnhancementDiscount + sumPaidAddonsUnitPriceForContext.
  return Math.max(0, list - Math.ceil(list * 0.1));
}

/** True when API member_price is lower than non_member_price (member savings). */
export function hasPaidAddonMemberDiscount(addon) {
  const memberPrice = Number(get(addon, 'member_price'));
  const nonMemberPrice = Number(get(addon, 'non_member_price'));
  return (
    Number.isFinite(memberPrice) &&
    Number.isFinite(nonMemberPrice) &&
    nonMemberPrice > memberPrice
  );
}

export function formatAddonCheckoutPrice(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  const whole = Math.round(n);
  if (Math.abs(n - whole) < 1e-9) return `$${whole}`;
  return `$${n.toFixed(2).replace(/[.,]00$/, '')}`;
}

/** Paid add-on label on booking circles/modals (whole dollars). */
export function formatAddonBookingPriceLabel(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '+$0';
  return `+$${Math.round(n)}`;
}

export function sumPaidAddonsUnitPriceForContext(
  addons,
  isMembership,
  isAlreadyMember,
) {
  const listSum = sumPaidAddonsListPrice(addons);
  const discount = getMemberPremiumEnhancementDiscount(
    addons,
    isMembership,
    isAlreadyMember,
  );
  return Math.max(0, listSum - discount);
}

export function hasAnyBookingPaidAddonSelection(appointmentData) {
  return getBookingSelectedPaidAddons(appointmentData).length > 0;
}

/**
 * Paid add-ons for the booking add-ons page (location-specific; excludes pref rows).
 */
export function getAddonsPagePaidAddons(addons, restrictionCtx = {}) {
  const visible = filterAddonsForBookingRestrictions(addons, restrictionCtx);
  return visible.filter(
    addon =>
      !isZeroPriceAddon(addon) &&
      !isComplimentaryPreferenceAddon(addon) &&
      !isDeepTissueAddon(addon),
  );
}

/** Paid catalog rows eligible for the checkout upsell modal. */
export function getCheckoutPromoPaidAddons(addons, restrictionCtx = {}) {
  return getAddonsPagePaidAddons(addons, restrictionCtx);
}

/** Paid add-ons eligible for checkout upsell (session length / guest restrictions). */
export function filterAddonsEligibleForCheckoutPromo(
  addons,
  restrictionCtx = {},
) {
  const {
    durationMinutes = null,
    pregnancy = false,
    guestUnder18 = false,
  } = restrictionCtx;
  const list = Array.isArray(addons) ? addons : [];
  return list.filter(
    addon =>
      !getAddonDisabledReason(addon, {
        durationMinutes,
        pregnancy,
        guestUnder18,
        selectedPaidAddons: [],
      }),
  );
}

/** Which paid add-on to show in the checkout recommendation modal. */
export function pickCheckoutPromoAddon(applicable) {
  const list = Array.isArray(applicable) ? applicable : [];

  const scalp = list.find(a => getAddonRuleKey(a) === 'scalp_ritual');
  if (scalp) return scalp;

  const cbd = list.find(a => getAddonRuleKey(a) === 'cbd_oil' || isCbdAddon(a));
  if (cbd) return cbd;

  return list[0] || null;
}

/**
 * Checkout upsell candidates: duration/restriction rules first, then therapist
 * capability. If the therapist only offers 80-min add-ons, fall back to any
 * duration-eligible paid add-on (e.g. CBD on a 50-min booking).
 */
export function getCheckoutPromoAddonCandidates(
  paidAddons,
  {
    restrictionCtx = {},
    therapistAddonIds = null,
    bypassTherapistFilter = false,
  } = {},
) {
  const durationEligible = filterAddonsEligibleForCheckoutPromo(
    paidAddons,
    restrictionCtx,
  );
  if (bypassTherapistFilter) {
    return durationEligible;
  }
  const therapistIds = Array.isArray(therapistAddonIds)
    ? therapistAddonIds
    : [];
  if (!therapistIds.length) {
    return durationEligible;
  }
  const therapistEligible = durationEligible.filter(addon => {
    const typeId = get(addon, 'addon_type.id');
    return therapistIds.some(tid => String(tid) === String(typeId));
  });
  return therapistEligible.length ? therapistEligible : durationEligible;
}

/**
 * Zenoti may send `exclusive_group` on addon_type; fallback keyword groups for
 * neck / hand / foot massage clusters.
 */
export function getAddonExclusiveGroup(addon) {
  if (!addon || typeof addon !== 'object') return null;
  const fromApi =
    get(addon, 'addon_type.exclusive_group') ||
    get(addon, 'addon_type.mutually_exclusive_group') ||
    get(addon, 'exclusive_group');
  if (fromApi != null && String(fromApi).trim() !== '') {
    return String(fromApi).trim();
  }
  const text = addonLabelText(addon);
  if (/\bneck\s+massage\b/.test(text)) return 'focus_area_massage';
  if (/\bhand\s+massage\b/.test(text)) return 'focus_area_massage';
  if (/\bfoot\s+massage\b/.test(text)) return 'focus_area_massage';
  return null;
}

/** Long marketing copy — not suitable for circle button labels. */
function isLikelyDescription(text) {
  if (text == null || text === '') return false;
  const s = String(text).trim();
  return s.length > 48 || /[.!?]\s+\w/.test(s) || s.split(/\s+/).length > 7;
}

function firstShortAddonTitleField(type) {
  if (!type || typeof type !== 'object') return '';
  const candidates = [
    get(type, 'label'),
    get(type, 'name'),
    get(type, 'title'),
    get(type, 'heading'),
  ];
  const short = candidates.find(t => t && !isLikelyDescription(t));
  if (short) return String(short).trim();
  const long = candidates.find(Boolean);
  if (!long) return '';
  const words = String(long)
    .trim()
    .split(/\s+/);
  return words.length > 4 ? words.slice(0, 4).join(' ') : String(long).trim();
}

/** Short title on booking add-on circle buttons. */
export function getAddonCircleLabel(addon) {
  if (!addon || typeof addon !== 'object') return 'Add-on';
  const ruleKey = getAddonRuleKey(addon);
  if (ruleKey && HARD_CODED_ADDON_DISPLAY_LABEL_BY_RULE_KEY[ruleKey]) {
    return HARD_CODED_ADDON_DISPLAY_LABEL_BY_RULE_KEY[ruleKey];
  }
  const label = firstShortAddonTitleField(get(addon, 'addon_type') || {});
  return label || 'Add-on';
}

/** Modal / upsell title (short name, same priority as circle). */
export function getAddonModalHeading(addon) {
  return getAddonCircleLabel(addon);
}

/** CBD amount disclaimer above addon modal buttons. */
export function getAddonModalCbdNote(addon) {
  const key = getAddonRuleKey(addon);
  return key ? HARD_CODED_ADDON_MODAL_CBD_NOTE_BY_RULE_KEY[key] || '' : '';
}

/** Description-only body copy for enhancement (?) info popups. */
export function getAddonInfoModalDescription(addon) {
  if (!addon || typeof addon !== 'object') return '';
  const type = get(addon, 'addon_type') || {};
  const candidates = [
    get(type, 'description'),
    get(type, 'more_info'),
    get(type, 'modal_description'),
    get(type, 'sub_heading'),
    get(addon, 'description'),
  ];
  const found = candidates.find(text => text && String(text).trim());
  return found ? String(found).trim() : '';
}

/** Body copy for addon more-info / upsell modal. */
export function getAddonModalSubheading(addon) {
  if (!addon || typeof addon !== 'object') return '';
  const type = get(addon, 'addon_type') || {};
  const heading = get(type, 'heading');
  const parts = [
    get(type, 'sub_heading'),
    get(type, 'description'),
    get(type, 'more_info'),
    get(type, 'modal_description'),
    heading && isLikelyDescription(heading) ? heading : null,
  ].filter(Boolean);
  return parts.join('\n\n');
}

/**
 * Add-on price shown in booking UI (+$X), respecting member vs pay-as-you-go context.
 */
export function getAddonModalDisplayPrice(
  addon,
  isMembership = false,
  isAlreadyMember = false,
) {
  const unit = getPaidAddonUnitPriceForContext(
    addon,
    isMembership,
    isAlreadyMember,
  );
  return unit != null ? unit : 0;
}

/** Static paths served from build/images/addons (see copy:addon-assets). */
export const BOOKING_ADDON_CIRCLE_BG_WHITE =
  '/images/addons/enhancement-white.svg';
export const BOOKING_ADDON_CIRCLE_BG_BLUE =
  '/images/addons/enhancement-blue.svg';

/**
 * Circle button background (API override or static enhancement SVGs).
 */
/** Webpack may export asset imports as `{ default: url }`. */
export function resolveWebpackAssetUrl(asset) {
  if (!asset) return '';
  if (typeof asset === 'string') return asset;
  if (typeof asset === 'object' && asset.default) {
    return typeof asset.default === 'string' ? asset.default : '';
  }
  return '';
}

export function getAddonCircleBackgroundUrl(
  addon,
  { selected = false, useStaticDefaults = true } = {},
) {
  const type = get(addon, 'addon_type') || {};
  const fromApi = selected
    ? get(type, 'selected_background_url') ||
      get(type, 'selected_circle_image_url') ||
      get(type, 'background_image_selected_url')
    : get(type, 'background_image_url') ||
      get(type, 'circle_background_url') ||
      get(type, 'circle_image_url');
  if (fromApi) return fromApi;
  if (!useStaticDefaults) return '';
  const fallback = selected
    ? BOOKING_ADDON_CIRCLE_BG_BLUE
    : BOOKING_ADDON_CIRCLE_BG_WHITE;
  return fallback;
}

function firstHttpAddonImageUrl(...candidates) {
  return candidates.find(
    url => typeof url === 'string' && /^https?:\/\//i.test(url.trim()),
  );
}

export function getAddonModalImageUrls(addon) {
  const type = get(addon, 'addon_type') || {};
  const desktopFromApi = firstHttpAddonImageUrl(
    get(type, 'desktop_image_url'),
    get(type, 'image_url'),
    get(type, 'modal_image_url'),
    get(addon, 'image_url'),
  );
  const mobileFromApi = firstHttpAddonImageUrl(
    get(type, 'mobile_image_url'),
    get(type, 'mobile_modal_image_url'),
    desktopFromApi,
  );
  if (desktopFromApi || mobileFromApi) {
    return {
      desktop: desktopFromApi || mobileFromApi,
      mobile: mobileFromApi || desktopFromApi,
    };
  }
  const bundled = getAddonCircleIconUrl(addon);
  return {
    desktop: bundled || '',
    mobile: bundled || '',
  };
}

export function collectAddonTypeIdsForBooking(appointmentData) {
  const {
    bookingSelectedFreeAddons,
    bookingMassagePreferences,
    selectedMassageType,
    serviceAddons,
    serviceAddonsLocationId,
    selectedLocation,
  } =
    appointmentData || {};
  const ids = [];
  const selectedId = get(selectedLocation, 'id');
  const list = Array.isArray(serviceAddons) ? serviceAddons : [];
  const addonsForLocation =
    selectedId != null &&
    serviceAddonsLocationId != null &&
    String(selectedId) === String(serviceAddonsLocationId)
      ? list
      : [];

  let prefsPlain = bookingMassagePreferences;
  if (prefsPlain && typeof prefsPlain.toJS === 'function') {
    prefsPlain = prefsPlain.toJS();
  }

  const freeFromPrefs = collectFreeAddonsFromBookingPreferences(
    addonsForLocation,
    prefsPlain,
    selectedMassageType,
  );
  const freeList = mergeBookingFreeAddonLists(
    bookingSelectedFreeAddons,
    freeFromPrefs,
  );

  freeList.forEach(a => {
    const tid = get(a, 'addon_type.id');
    if (tid != null && tid !== '') ids.push(String(tid));
  });
  getBookingSelectedPaidAddons(appointmentData).forEach(a => {
    const tid = get(a, 'addon_type.id');
    if (tid != null && tid !== '') ids.push(String(tid));
  });
  const unique = [...new Set(ids)];
  return unique.length ? unique.join(',') : undefined;
}

/** Booked / my-appointments API rows under `appointment.addon_services`. */
export function getAppointmentAddonServiceRows(appointment) {
  const raw = get(appointment, 'addon_services');
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }
  return raw.filter(
    row => row && (get(row, 'addon_service') || get(row, 'addon_type')),
  );
}

/**
 * Whether to show member vs non-member addon prices on a saved appointment.
 * Pass `useMemberPricing` to override; otherwise uses `had_membership` or `userHasMembership`.
 */
export function resolveMemberPricingForAppointment(appointment, options = {}) {
  if (typeof options.useMemberPricing === 'boolean') {
    return options.useMemberPricing;
  }
  if (options.userHasMembership != null) {
    return !!options.userHasMembership;
  }
  return !!get(appointment, 'had_membership');
}

/** One line for confirmation / list / modal (e.g. "$15 CBD Oil" or "Deep Tissue" when $0). */
export function formatAppointmentAddonServiceRow(row, useMemberPricing) {
  const svc = get(row, 'addon_service') || row;
  const label =
    get(svc, 'addon_type.label') ||
    get(svc, 'addon_type.heading') ||
    getAddonCircleLabel(svc) ||
    'Add-on';
  const priceRaw = useMemberPricing
    ? get(svc, 'member_price')
    : get(svc, 'non_member_price');
  const price = Number(priceRaw);
  if (!Number.isFinite(price) || price === 0) {
    return label;
  }
  return `$${price} ${label}`;
}

/** Comma-separated addon summary for compact list rows. */
export function getAppointmentAddonSummaryText(appointment, options = {}) {
  const useMember = resolveMemberPricingForAppointment(appointment, options);
  return getAppointmentAddonServiceRows(appointment)
    .map(row => formatAppointmentAddonServiceRow(row, useMember))
    .join(', ');
}
